import React, { useMemo, useEffect } from "react";
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import GraphNode from "@/components/explore/GraphNode";
import { radialLayout, clusterLayout } from "@/lib/graphLayout";

const nodeTypes = { entity: GraphNode };

export function ExploreGraph({ state, visibleIds, nodeMap, edges, onInit, myLayer, noteCounts, onDrillIn, overview }) {
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);

  const visKey = useMemo(() => Array.from(visibleIds).sort().join(","), [visibleIds]);

  const layout = useMemo(
    () => (overview ? clusterLayout(visibleIds, nodeMap) : radialLayout(visibleIds, edges, state.rootId)),
    [visKey, edges, state.rootId, overview] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const ids = Array.from(visibleIds);
    setRfNodes(
      ids
        .filter((id) => nodeMap[id])
        .map((id) => ({
          id,
          type: "entity",
          position: layout[id] || { x: 0, y: 0 },
          data: {
            id,
            label: nodeMap[id].label,
            type: nodeMap[id].type,
            selected: id === state.selectedId,
            onSelect: state.select,
            onFocus: onDrillIn || state.focus,
            myLayer,
            noteCount: (noteCounts && noteCounts[id]) || 0,
          },
        }))
    );
    setRfEdges(
      edges
        .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
        .map((e) => {
          const touchesSelected =
            e.source === state.selectedId || e.target === state.selectedId;
          return {
            id: e.id,
            source: e.source,
            target: e.target,
            label: touchesSelected ? e.label : undefined,
            type: "default",
            animated: touchesSelected,
            markerEnd: { type: MarkerType.ArrowClosed, color: touchesSelected ? "#ffb020" : "#b8ab93" },
            style: {
              stroke: touchesSelected ? "#ffb020" : "#c9bda6",
              strokeWidth: touchesSelected ? 2 : 1.25,
            },
            labelStyle: { fill: "#4e6073", fontSize: 11, fontWeight: 600 },
            labelBgStyle: { fill: "#fdfdfb", fillOpacity: 0.9 },
          };
        })
    );
  }, [visKey, layout, state.selectedId, edges, nodeMap, setRfNodes, setRfEdges, myLayer, noteCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ReactFlow
      nodes={rfNodes}
      edges={rfEdges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onInit={onInit}
      onNodeClick={(_, node) => state.select(node.id)}
      onNodeDoubleClick={(_, node) => (onDrillIn ? onDrillIn(node.id) : state.focus(node.id))}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable
      selectNodesOnDrag={false}
      nodeDragThreshold={1}
      panOnDrag
      fitView
      minZoom={0.2}
      maxZoom={1.8}
      proOptions={{ hideAttribution: true }}
      className="lw-flow"
    >
      <Background variant={BackgroundVariant.Dots} gap={26} size={1} color="#d8ccb4" />
    </ReactFlow>
  );
}
